import React from "react";

export function LegalLayout({ overline, title, children }) {
  return (
    <div className="text-white">
      <section className="border-b border-white/10 bg-[#0A0A0A]">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 py-20">
          <div className="overline mb-3">{overline}</div>
          <h1 className="font-display text-4xl sm:text-5xl">{title}</h1>
        </div>
      </section>
      <section className="max-w-4xl mx-auto px-5 sm:px-8 py-16 prose prose-invert prose-p:text-neutral-300 prose-headings:font-display prose-headings:text-white text-neutral-300 leading-relaxed space-y-6">
        {children}
      </section>
    </div>
  );
}

export function About() {
  return (
    <div data-testid="about-page" className="text-white">
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-40"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1786041176778-14825b13576f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjBtb2Rlcm4lMjBhcmNoaXRlY3R1cmUlMjBkYXJrfGVufDB8fHx8MTc4NzYxNzkzMHww&ixlib=rb-4.1.0&q=85')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#050505]/70 via-[#050505]/80 to-[#050505]" />
        <div className="relative max-w-5xl mx-auto px-5 sm:px-8 py-24 lg:py-32">
          <div className="overline mb-4">Firm</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-tight max-w-3xl">
            A discreet venue for allocators who prefer restraint.
          </h1>
        </div>
      </section>
      <section className="max-w-5xl mx-auto px-5 sm:px-8 py-16 grid grid-cols-1 lg:grid-cols-2 gap-12 text-neutral-300 leading-relaxed">
        <div>
          <div className="overline mb-3">Origin</div>
          <p className="mb-4">
            Madrefajona Reserved was founded in Zurich by a group of private
            bankers, cryptographers and physical-commodities traders united by
            a shared frustration: allocators were being offered noise instead of
            books.
          </p>
          <p>
            The result is a small, deliberately restrained venue that pairs
            institutional custody with a modern digital dashboard — no vanity
            metrics, no leverage on your capital.
          </p>
        </div>
        <div>
          <div className="overline mb-3">Principles</div>
          <ul className="space-y-4">
            <li>
              <span className="text-white font-medium">Discretion.</span>{" "}
              We publish quarterly attestations, not press releases.
            </li>
            <li>
              <span className="text-white font-medium">Segregation.</span>{" "}
              Your assets are yours. Never rehypothecated, never comingled.
            </li>
            <li>
              <span className="text-white font-medium">Inclusion.</span>{" "}
              Clients registered as living with a disability receive a 5% bonus
              on their first deposit — coded into the platform, not a marketing
              gimmick.
            </li>
            <li>
              <span className="text-white font-medium">Clarity.</span>{" "}
              Every asset ships with a plainly-written risk disclosure.
            </li>
          </ul>
        </div>
      </section>
    </div>
  );
}

export function FAQ() {
  const faqs = [
    ["Is Madrefajona Reserved a bank?", "No. We are a private digital asset venue. Cash balances are simulated in this MVP; production custody is handled by regulated partner institutions."],
    ["Are crypto prices live?", "Yes. Crypto marks stream from CoinGecko's institutional feed and refresh at least once per minute."],
    ["How is the 5% inclusion bonus applied?", "During registration or in account settings, mark yourself as living with a disability. The bonus is credited automatically on your first deposit."],
    ["Are SpaceX-related notes real equity?", "Frontier positions are structured as private secondary notes referencing SpaceX or Starlink exposure. They are illiquid, long-duration and restricted to accredited investors."],
    ["Can I withdraw at any time?", "Cash withdrawals and liquid crypto/gold/stock positions can be redeemed at any time. Frontier positions have lock-up windows disclosed on each asset page."],
    ["What are the fees?", "There are no platform fees for the MVP. Production tariff schedules will be published in your dashboard prior to any change."],
  ];
  const [open, setOpen] = React.useState(0);
  return (
    <LegalLayout overline="Reserved · FAQ" title="Frequently asked.">
      <div className="not-prose divide-y divide-white/10 border-y border-white/10">
        {faqs.map(([q, a], i) => (
          <div key={q} data-testid={`faq-${i}`} className="py-6">
            <button
              onClick={() => setOpen(open === i ? -1 : i)}
              className="w-full flex justify-between items-center text-left"
            >
              <span className="font-display text-xl text-white">{q}</span>
              <span className="text-[#E6C998] text-2xl leading-none">{open === i ? "−" : "+"}</span>
            </button>
            {open === i && (
              <p className="mt-4 text-neutral-400 text-base leading-relaxed">{a}</p>
            )}
          </div>
        ))}
      </div>
    </LegalLayout>
  );
}

export function Terms() {
  return (
    <LegalLayout overline="Legal" title="Terms of Service">
      <p><em>Last updated: February 2026.</em></p>
      <p>These Terms govern your access to and use of Madrefajona Reserved (the “Platform”). By opening an account you accept these Terms in full.</p>
      <h3>1. Eligibility</h3>
      <p>You must be 18+ and legally able to enter into a binding contract. Certain assets (e.g., frontier notes) require accredited-investor status.</p>
      <h3>2. Account</h3>
      <p>You are responsible for the security of your credentials. Notify us immediately of any suspected unauthorised access.</p>
      <h3>3. Simulated MVP</h3>
      <p>Deposits and withdrawals inside the MVP are simulated. No real money movement occurs.</p>
      <h3>4. No advice</h3>
      <p>Nothing on the Platform constitutes investment, tax or legal advice. Consult a qualified professional.</p>
      <h3>5. Limitation of liability</h3>
      <p>To the fullest extent permitted by law, we exclude liability for indirect or consequential losses.</p>
      <h3>6. Changes</h3>
      <p>We may update these Terms. Material changes will be surfaced inside your dashboard.</p>
    </LegalLayout>
  );
}

export function Privacy() {
  return (
    <LegalLayout overline="Legal" title="Privacy Policy">
      <p><em>Last updated: February 2026.</em></p>
      <p>Madrefajona Reserved values discretion. This policy describes what we collect, how we use it, and your rights.</p>
      <h3>Data we collect</h3>
      <ul>
        <li>Account data: name, email, hashed password.</li>
        <li>Transaction data: simulated deposits, withdrawals, allocations.</li>
        <li>Inclusion flag: optional self-declaration for the 5% first-deposit bonus.</li>
      </ul>
      <h3>How we use it</h3>
      <p>To operate the Platform, provide client services, and satisfy regulatory obligations.</p>
      <h3>Retention</h3>
      <p>We retain data as long as required by law or for legitimate business needs.</p>
      <h3>Your rights</h3>
      <p>You may request access, correction, or deletion of your personal data by contacting our desk.</p>
    </LegalLayout>
  );
}

export function Risk() {
  return (
    <LegalLayout overline="Regulatory" title="Risk Disclosure">
      <p><strong>Investing involves risk of loss, including the loss of principal.</strong></p>
      <p>The following risks apply across the categories offered by Madrefajona Reserved:</p>
      <h3>Crypto</h3>
      <p>Digital assets are highly volatile, traded 24/7, and can experience severe drawdowns within short windows. Custody, regulation and taxation may vary by jurisdiction.</p>
      <h3>Gold</h3>
      <p>Gold prices respond to macroeconomic, monetary and geopolitical conditions. Storage, insurance and delivery costs may apply on physical redemption.</p>
      <h3>Digital Stocks</h3>
      <p>Digital-share wrappers reference underlying listed equities. They are not direct shareholdings and do not confer voting rights. Corporate actions are applied on a best-effort basis.</p>
      <h3>SpaceX-related notes</h3>
      <p>Frontier positions are structured as private secondary instruments. They are illiquid, long-duration, and typically restricted to accredited or sophisticated investors. Total loss of capital is possible.</p>
      <h3>General</h3>
      <p>Past performance is not indicative of future results. Any targeted returns are illustrative and not guaranteed. Consult independent advisers before allocating capital.</p>
    </LegalLayout>
  );
}
